/**
 * Recurring Expense Edit Modal
 * 
 * Modal for editing existing recurring expense templates.
 * Changes apply to future expenses only - past expenses remain unchanged.
 */

import { useState, useEffect } from 'react';
import { X, Info } from 'lucide-react';
import type { RecurringExpense, RecurringFrequency, CategoryViewModel, CreateCategoryDto } from '@/lib/domains';
import { CategoryService } from '@/lib/domains/category';
import { AmountInput } from './form/amount-input';
import { CategoryInput } from './form/category-input';
import { RecurringExpenseFields } from './form/recurring-expense-fields';
import { DescriptionInput } from './form/description-input';
import { VALIDATION_RULES, ERROR_MESSAGES } from '@/lib/constants';
import { handleError } from '@/lib/utils/error-handler';

interface RecurringExpenseEditModalProps {
    recurring: RecurringExpense;
    categories: CategoryViewModel[];
    onSave: (updates: {
        amount?: number;
        currency?: string;
        categoryId?: string;
        description?: string;
        frequency?: RecurringFrequency;
        endDate?: string;
    }) => Promise<void>;
    onCancel: () => void;
}

export function RecurringExpenseEditModal({
    recurring,
    categories,
    onSave,
    onCancel
}: RecurringExpenseEditModalProps) {
    const [formData, setFormData] = useState({
        amount: recurring.amount.toString(),
        currency: recurring.currency,
        categoryId: recurring.categoryId || '',
        description: recurring.description || '',
        frequency: recurring.frequency,
        endDate: recurring.endDate || ''
    });
    const [errors, setErrors] = useState<{ amount?: string; category?: string; endDate?: string }>({});
    const [isSaving, setIsSaving] = useState(false);
    const [recentCategoryIds, setRecentCategoryIds] = useState<string[]>([]);

    useEffect(() => {
        // Load recent categories for selector
        const loadRecentCategories = async () => {
            try {
                const { getSupabase } = await import('@/lib/database');
                const supabase = getSupabase();
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) return;

                const recent = await CategoryService.getRecentCategoriesWithPresentation(user.id, 4, 'light');
                setRecentCategoryIds(recent.map(c => c.id));
            } catch (err) {
                console.error('Failed to load recent categories:', err);
            }
        };
        loadRecentCategories();
    }, []);

    const handleCreateCategory = async (dto: CreateCategoryDto) => {
        const { getSupabase } = await import('@/lib/database');
        const supabase = getSupabase();
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const newCat = await CategoryService.createCategory(user.id, dto);
        const newCategoryWithPresentation = await CategoryService.getCategoryById(newCat.id, user.id, 'light');
        if (newCategoryWithPresentation) {
            setFormData(prev => ({ ...prev, categoryId: newCategoryWithPresentation.id }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: typeof errors = {};

        const amountValue = parseFloat(formData.amount);
        if (!formData.amount || amountValue <= 0) {
            newErrors.amount = ERROR_MESSAGES.AMOUNT_REQUIRED;
        } else if (amountValue > VALIDATION_RULES.MAX_AMOUNT) {
            newErrors.amount = ERROR_MESSAGES.AMOUNT_TOO_LARGE;
        }

        if (!formData.categoryId) {
            newErrors.category = ERROR_MESSAGES.CATEGORY_REQUIRED;
        }

        // Validate end date is not before next due date
        if (formData.endDate && formData.endDate < recurring.nextDueDate) {
            newErrors.endDate = `End date cannot be before next scheduled expense (${recurring.nextDueDate})`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSaving(true);

        try {
            const updates: any = {};

            const amountValue = parseFloat(formData.amount);
            if (amountValue !== recurring.amount) {
                updates.amount = amountValue;
            }

            if (formData.currency !== recurring.currency) {
                updates.currency = formData.currency;
            }

            if (formData.categoryId !== recurring.categoryId) {
                updates.categoryId = formData.categoryId;
            }

            if (formData.description !== recurring.description) {
                updates.description = formData.description.trim() || undefined;
            }

            if (formData.frequency !== recurring.frequency) {
                updates.frequency = formData.frequency;
            }

            if (formData.endDate !== recurring.endDate) {
                updates.endDate = formData.endDate || undefined;
            }

            await onSave(updates);
            onCancel();
        } catch (err: unknown) {
            setErrors({ amount: handleError(err, 'Failed to save changes') });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900">Edit Recurring Expense</h2>
                    <button
                        onClick={onCancel}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Close modal"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                    {/* Info Banner */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <div className="flex gap-3">
                            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm">
                                <p className="font-medium text-blue-900 mb-1">
                                    Changes apply to future expenses only
                                </p>
                                <p className="text-blue-700">
                                    Past expenses that were already generated will remain unchanged.
                                    This preserves your financial history.
                                </p>
                            </div>
                        </div>
                    </div>

                    <AmountInput
                        amount={formData.amount}
                        currency={formData.currency}
                        onAmountChange={(amount) => {
                            setFormData({ ...formData, amount });
                            if (errors.amount) setErrors({ ...errors, amount: undefined });
                        }}
                        onCurrencyChange={(currency) => setFormData({ ...formData, currency })}
                        error={errors.amount}
                    />

                    <CategoryInput
                        categories={categories}
                        selectedCategoryId={formData.categoryId}
                        recentCategoryIds={recentCategoryIds}
                        onSelect={(categoryId) => {
                            setFormData({ ...formData, categoryId });
                            if (errors.category) setErrors({ ...errors, category: undefined });
                        }}
                        onCreateCategory={handleCreateCategory}
                        error={errors.category}
                    />

                    <RecurringExpenseFields
                        frequency={formData.frequency}
                        endDate={formData.endDate}
                        minEndDate={recurring.nextDueDate}
                        onFrequencyChange={(frequency) => setFormData({ ...formData, frequency })}
                        onEndDateChange={(endDate) => {
                            setFormData({ ...formData, endDate });
                            if (errors.endDate) setErrors({ ...errors, endDate: undefined });
                        }}
                        showFrequencyWarning={formData.frequency !== recurring.frequency}
                        endDateError={errors.endDate}
                    />

                    <DescriptionInput
                        value={formData.description}
                        onChange={(description) => setFormData({ ...formData, description })}
                    />

                    {/* Actions */}
                    <div className="flex gap-3 justify-end">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="px-6 py-2 text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
                        >
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
